<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="t('campaignsAutomationList.actions.activate')"
  >
    <div class="p-fluid">
      <p>{{ t('campaignsAutomationList.actions.activateConfirm') }}</p>
      <p
        v-if="currentCampaign"
        class="font-semibold mt-2"
      >
        {{ currentCampaign.name }}
      </p>
    </div>
    <template #footer>
      <SpmButton
        :label="t('no')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('yes')"
        icon="pi pi-check"
        class-style="p-button p-button-success"
        :disabled="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import moment from 'moment';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { CampaignAutomationStatusEnum, TypeCampaignEnum } from '@/types';
import {
  updateCampaignAutomationListColumn,
  setRefreshScenariosList,
  toggleWebhooksForAutomationCampaign,
  togglePeriodicTriggersForAutomationCampaign,
} from '@/composables/AutomatedScenarios';
import { useNodeWarnings } from '@/composables/flows';
import { fetchFlowStructure } from '@/composables/flows/useFlowApi';
import type { CampaignsAutomationUpdateInputItem } from '@/types/generated-types/graphql';
import type { AutomationCampaignData, CampaignActionPayload, ActivateCampaignResult } from './types';

export default defineComponent({
  name: 'ActivateCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();
    const router = useRouter();
    const { getFlowBlockingWarnings } = useNodeWarnings();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentCampaign = ref<AutomationCampaignData | null>(null);
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      // Check if shop has valid offer
      const hasOfferV4 = UserState.activeOffer && Object.keys(UserState.activeOffer).length > 0;
      const hasOfferV3 = UserState.hasOfferV3 === true && UserState.offerV3 && ['1', '3'].includes(UserState.offerV3.bundle_options);

      if (!hasOfferV4 && !hasOfferV3) {
        // No valid offer - redirect to offer page
        showToastError(t('automatedScenarios.insufficientOffer'));
        router.push({ name: 'shop.MyOffer' });
        return;
      }

      currentCampaign.value = payload.campaign;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentCampaign.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentCampaign.value) return;

      isLoading.value = true;

      try {
        // Never activate a scenario whose flow has blocking warnings
        // (incompatible boxes, incomplete config). Same guard as the editor,
        // reproduced here because the list toggle bypasses the flow editor.
        const flow = await fetchFlowStructure(TypeCampaignEnum.AUTOMATION, currentCampaign.value.id);
        const blocking = await getFlowBlockingWarnings(flow, TypeCampaignEnum.AUTOMATION, idShop.value);
        if (blocking.length > 0) {
          showToastError(t('campaignsAutomationList.actions.activateBlockingWarnings'));
          return;
        }

        const input: CampaignsAutomationUpdateInputItem = {
          id_campaign_automation: currentCampaign.value.id,
          id_shop: idShop.value,
          status: CampaignAutomationStatusEnum.ACTIVE,
          date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
        };

        await updateCampaignAutomationListColumn([input]);
        // Activation/deactivation of workflow's webhooks
        toggleWebhooksForAutomationCampaign(currentCampaign.value.id, true);
        // Activation/deactivation of workflow's periodic triggers
        togglePeriodicTriggersForAutomationCampaign(currentCampaign.value.id, true);
        showToastSuccess(t('campaignsAutomationList.actions.activateSuccess'));
        setRefreshScenariosList(true);

        const result: ActivateCampaignResult = {
          campaign: currentCampaign.value,
          success: true,
        };

        emit('confirm', result);
        close();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('errorMessages.GENERIC_ERROR');
        showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      currentCampaign,
      close,
      confirm,
    };
  },
});
</script>
